import type { TournamentInformationsSchema } from "@teamz/common";

import AddIcon from "@/assets/add.svg";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { matchAndClubCountryCodes, tournamentInformationsSchema } from "@teamz/common";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import type { RouterOutputs } from "@/lib/trpc";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetClose, SheetContent, SheetFooter, SheetForm, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { ErrorField, RootField, TextAreaField, TextField } from "@/components/ui/text-field";
import { useMe } from "@/context/user-context";
import { useTranslationUtils } from "@/hooks/use-translation-utils";
import { tDyn } from "@/lib/i18n-dynamic";
import { useTRPC } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { routes } from "@/routes";

export function UpsertTournamentForm({ tournament, closeModal }: { tournament?: Omit<RouterOutputs["tournament"]["getTournament"], "tournamentPlayers">; closeModal: () => void }) {
  const { t } = useTranslation(["common", "tournaments"]);
  const { getCountryName } = useTranslationUtils();
  const [descriptionLength, setDescriptionLength] = useState(0);
  const navigate = useNavigate();
  const today = new Date().toISOString().split("T")[0];
  const { user } = useMe();
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation(trpc.tournament.upsertTournament.mutationOptions({
    onSuccess: (data) => {
      closeModal();
      queryClient.invalidateQueries(trpc.tournament.getTournamentList.queryFilter());
      queryClient.invalidateQueries(trpc.tournament.getTournament.queryFilter());
      navigate(routes.getTournamentPath(data), { state: { fromPath: routes.tournaments } });
    },
  }));

  const tournamentForm = useForm<TournamentInformationsSchema>({
    resolver: zodResolver(tournamentInformationsSchema),
    defaultValues: {
      name: tournament?.name || "",
      imgUrl: user.imgUrl,
      city: tournament?.city || "",
      country: tournament?.country || "fr",
      description: tournament?.description || "",
      startDate: tournament?.startDate ? new Date(tournament.startDate).toISOString().split("T")[0] : "",
      endDate: tournament?.endDate ? new Date(tournament.endDate).toISOString().split("T")[0] : "",
    },
  });

  const onSubmit = tournamentForm.handleSubmit((data) => {
    mutate({ tournamentId: tournament?.id || undefined, tournamentInformationsSchema: { ...data } });
  });

  return (
    <>
      <SheetForm id="TournamentForm" onSubmit={onSubmit}>
        <TextField
          label={t("tournaments:tournament-name")}
          {...tournamentForm.register("name")}
          errorMessage={tDyn(t, tournamentForm.formState.errors.name?.message)}
        />

        <TextField
          label={t("common:city")}
          {...tournamentForm.register("city")}
          errorMessage={tDyn(t, tournamentForm.formState.errors.city?.message)}
        />

        <Controller
          control={tournamentForm.control}
          name="country"
          defaultValue="fr"
          render={({ field, fieldState }) => (
            <RootField>
              <Label>{t("common:country")}</Label>
              <Select
                value={field.value}
                onValueChange={field.onChange}
                disabled
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>

                <SelectContent>
                  <SelectGroup>
                    {matchAndClubCountryCodes.filter(code => code === "fr").map(country => (
                      <SelectItem key={country} value={country}>
                        {getCountryName(country)}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              {fieldState.error?.message && (
                <ErrorField>{tDyn(t, fieldState.error?.message)}</ErrorField>
              )}
            </RootField>
          )}
        />

        <Controller
          control={tournamentForm.control}
          name="startDate"
          render={({ field, fieldState }) => (
            <RootField>
              <Label>{t("tournaments:tournament-start-date")}</Label>
              <TextField
                type="date"
                min={today}
                value={field.value || ""}
                onChange={(event) => {
                  field.onChange(event.target.value);
                  tournamentForm.setValue("endDate", "");
                }}
              />
              {fieldState.error?.message && (
                <ErrorField>{tDyn(t, fieldState.error?.message)}</ErrorField>
              )}
            </RootField>
          )}
        />

        <TextField
          label={t("tournaments:tournament-end-date")}
          disabled={!tournamentForm.watch("startDate")}
          min={tournamentForm.getValues("startDate")}
          type="date"
          errorMessage={tDyn(t, tournamentForm.formState.errors.endDate?.message)}
          {...tournamentForm.register("endDate")}
        />

        <TextAreaField
          label={(
            <span className="inline-flex w-full justify-between child:block">
              <span>Description</span>
              <span className={cn("text-neutral-450", descriptionLength > 730 && "text-orange-300", descriptionLength === 750 && "text-orange-500")}>
                {descriptionLength}
                /750
              </span>
            </span>
          )}
          errorMessage={tDyn(t, tournamentForm.formState.errors.description?.message)}
          {...tournamentForm.register("description", {
            onChange(event) {
              if (event.target.value.trim().length > 750) {
                event.target.value = event.target.value.trim().slice(0, 750);
              }
              if (event.target.value.trim().length === 0) {
                event.target.value = "";
              }
              setDescriptionLength(event.target.value.length);
            },
          })}
        />
      </SheetForm>

      <SheetFooter>
        <SheetClose asChild>
          <Button variant="dark" type="button">
            {t("common:cancel")}
          </Button>
        </SheetClose>
        <Button type="submit" form="TournamentForm" loading={isPending || tournamentForm.formState.isSubmitting}>
          {t("common:save")}
        </Button>
      </SheetFooter>
    </>
  );
}

export function UpsertTournament({ className }: { className?: string }) {
  const [modalState, updateModalState] = useState<"open" | "close">("close");
  const { t } = useTranslation(["tournaments"]);

  return (
    <Sheet open={modalState === "open"} onOpenChange={isOpen => updateModalState(isOpen ? "open" : "close")}>
      <SheetTrigger asChild>
        <Button className={cn("w-60", className)}>
          <AddIcon />
          {t("tournaments:create-tournament")}
        </Button>
      </SheetTrigger>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>
            {t("tournaments:create-tournament")}
          </SheetTitle>
        </SheetHeader>

        <UpsertTournamentForm closeModal={() => updateModalState("close")} />

      </SheetContent>
    </Sheet>
  );
}
